import { useEffect, useState } from "react"
import { DetailPage, Row } from "../../components/crud/DetailPage"
import { ListPage } from "../../components/crud/ListPage"
import { DateInput, EmailInput, Select, TextareaInput, TextInput } from "../../components/forms/Inputs"
import { useTranslations } from "../../layers/Localization"
import { ClientsList, TvaInput } from "./Clients"
import { ContactsList } from "./Contacts"
import { useApi } from "../../hooks/useApi"
import { HistoriquesList } from "./Historique"
import { postalCodeToCommune } from "../../hooks/helpers"
import { useToday } from "../../hooks/useToday"

export const useLangueOptions = () => {
    const t = useTranslations()
    return [
        { value: 'FR', label: t('FR') },
        { value: 'NL', label: t('NL') },
        { value: 'EN', label: t('EN') },
    ]
}

export const useGerantTypeOptions = () => {
    const t = useTranslations()
    return [
        { value: 'SYNDICPRO', label: t('Syndic pro') },
        { value: 'SYNDICPRIVE', label: t('Syndic privé') },
        { value: 'PROPRIETAIRE', label: t('Propriétaire') },
        { value: 'DESTINATAIRE', label: t('Destinataire') },
        { value: 'AUTRE', label: t('Autre') },
    ]
}

function GerantFields({ item, set, tab }) {
    const [clients, setClients] = useState([])

    const api = useApi()
    const t = useTranslations()
    const langueOptions = useLangueOptions()
    const gerantTypeOptions = useGerantTypeOptions()

    useEffect(() => {
        if (item.id) api.get(`/clients?gerantId=${encodeURIComponent(item.id)}`).then(setClients)
    }, [item.id])

    const openClients = clients.filter(x => !x.dateDernierTravail)
    const invalidLastDate = item.dateDernierTravail && openClients.length && t("Impossible de fermer le gérant car il reste des clients non fermés: {clients}", { clients: openClients.map(x => x.nom).join(', ') })

    const onCodePostalChange = value => {
        set('cp')(value)
        set('commune')(postalCodeToCommune(value))
    }

    return <>
        <Row>
            <TextInput className="grow" label={t("Nom")} value={item.nom} onChange={set('nom')} required />
            <Select label={t("Type")} value={item.type} onChange={set('type')} options={gerantTypeOptions} />
            <TvaInput label={t("Numéro de TVA")} value={item.numTva} onChange={set('numTva')} />
        </Row>
        <Row>
            <TextInput label={t("Adresse")} value={item.adresse} onChange={set('adresse')} className="grow" required />
            <TextInput label={t("Code postal")} value={item.cp} onChange={onCodePostalChange} required />
            <TextInput label={t("Commune")} value={item.commune} onChange={set('commune')} required />
        </Row>
        <hr />
        <Row>
            <Select label={t("Langue")} value={item.langue} onChange={set('langue')} options={langueOptions} required />
            <EmailInput className="grow" label={t("Email facturation")} value={item.emailFacturation} onChange={set('emailFacturation')} />
            <EmailInput className="grow" label={t("Email rappels")} value={item.emailRappels} onChange={set('emailRappels')} />
        </Row>
        <hr />
        <Row>
            <DateInput label={t("Date de premier travail")} value={item.datePremierTravail} onChange={set('datePremierTravail')} />
            <DateInput label={t("Date de dernier travail")} value={item.dateDernierTravail} onChange={set('dateDernierTravail')} invalid={invalidLastDate} />
        </Row>
        <hr />
        <TextareaInput className="[&_textarea]:text-red-600" label={t("Mémo interne")} value={item.memoInterne} onChange={set('memoInterne')} />
    </>
}

export function GerantsList(props) {
    const t = useTranslations()
    const gerantTypeOptions = useGerantTypeOptions()
    const today = useToday()

    return <ListPage model="gerants" title={t("Gérants")} columns={{
        [t('Nom')]: 'nom',
        [t("Type")]: { sort: 'type', value: item => gerantTypeOptions.find(option => option.value === item?.type)?.label },
        [t('Code postal')]: 'cp',
        [t('Langue')]: 'langue',
    }} filter={{
        [t('Actifs')]: `or2:datePremierTravail:lte=${encodeURIComponent(today.toISOString())}&or2:datePremierTravail=&or3:dateDernierTravail:gt=${encodeURIComponent(today.toISOString())}&or3:dateDernierTravail=`,
        [t('Passés')]: `dateDernierTravail:lte=${encodeURIComponent(today.toISOString())}`,
        [t('Futurs')]: `datePremierTravail:gte=${encodeURIComponent(today.toISOString())}`,
        [t('Tous')]: ``,
    }} {...props} />
}

export function GerantDetail(props) {
    const t = useTranslations()
    return <DetailPage model="gerants" title={t("Gérant")} Fields={GerantFields} tables={{
        [t("Contacts")]: <ContactsList relation="gerantId" root="." />,
        [t("Historique")]: <HistoriquesList relation="gerantId" root="." />,
        [t("Clients")]: <ClientsList relation="gerantId" />,
    }} {...props} />
}
